import { DecimalPipe, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
  computed,
  inject,
  signal
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { I18nService } from '../../core/i18n.service';
import { SiteConfigService } from '../../core/site-config.service';
import { ResponsiveImageComponent } from '../../shared/responsive-image.component';
import { FeedLivePlayerComponent } from './feed-live-player.component';
import { FeedComment, FeedItem, FeedsService } from './feeds.service';
import { ApiClientService } from '../../core/api-client.service';
import { LiveCallService } from '../../core/live-call.service';
import { firstValueFrom } from 'rxjs';
import { SeoService } from '../../core/seo.service';
import { PageDataService } from '../../core/page-data.service';
import { ThemeListExperienceService } from '../../theme/theme-list-experience.service';

interface CommentState {
  loading: boolean;
  submitting: boolean;
  text: string;
  items: FeedComment[];
  hasMore: boolean;
  error: string;
}

@Component({
  selector: 'app-feeds-widget',
  imports: [DecimalPipe, ResponsiveImageComponent, RouterLink, FeedLivePlayerComponent],
  template: `
    <section class="feeds-widget" [class.watch-mode]="watchMode" [attr.data-theme-feed-layout]="themeFeedSettings()['layout'] || 'stream'">
      <div class="feeds-hero">
        <p class="eyebrow">{{ themeFeedText('eyebrow',copy().eyebrow) }}</p>
        @if (embedded) {
          <h2>{{ themeFeedText('heading',copy().title) }}</h2>
        } @else {
          <h1>{{ watchMode ? watchTitle() : themeFeedText('heading',copy().title) }}</h1>
        }
        <p>{{ watchMode ? watchDescription() : themeFeedText('lead',copy().lead) }}</p>
      </div>

      <div class="feed-stream">
        @for (feed of visibleItems(); track feed.feedID) {
          <article class="feed-card" [class.live-card]="feed.live?.isLive">
            <header class="feed-head">
              @if (themeFeedEnabled('show_seller',true)) {
              <a class="seller-pill" [routerLink]="sellerLink(feed)">
                <span class="avatar">
                  @if (feed.sellerAvatarUrl) {
                    <app-responsive-image
                      [url]="feed.sellerAvatarUrl"
                      [alt]="feedSellerName(feed)"
                      displaySizes="56px"
                    />
                  } @else {
                    <span>{{ initials(feedSellerName(feed)) }}</span>
                  }
                </span>
                <span>
                  <strong>{{ feedSellerName(feed) }}</strong>
                  <small>{{ feed.sellerHandle || copy().reader }}</small>
                </span>
              </a>
              }

              @if (site.enabled('auth')) {
                <button type="button" class="follow-btn" [class.active]="feed.isFollowing" (click)="toggleFollow(feed)">
                  {{ feed.isFollowing ? copy().following : copy().followReader }}
                </button>
              }
              @if (callAvailability(feed.sellerID); as available) {
                <span class="feed-call-actions">
                  @if (available.voiceEnabled) { <button type="button" (click)="calls.start(feed.sellerID, 'voice')" aria-label="Sesli ara">☎</button> }
                  @if (available.videoEnabled) { <button type="button" (click)="calls.start(feed.sellerID, 'video')" aria-label="Görüntülü ara">▣</button> }
                </span>
              }
            </header>

            <div class="media-shell">
              @if (feed.live?.isLive) {
                <div class="live-ribbon">
                  <span></span>
                  {{ copy().liveNow }}
                  <small>{{ feed.live?.viewerCount || 0 }} {{ copy().viewer }}</small>
                </div>
              }

              @if (feed.live?.isLive) {
                <app-feed-live-player [feed]="feed" />
              } @else if (feed.live?.replayUrl) {
                <video
                  [src]="mediaUrl(feed.live?.replayUrl)"
                  controls
                  playsinline
                  preload="none"
                  [poster]="posterUrl(feed.live?.coverImageUrl || feed.imageUrl || feed.listingImageUrl)"
                ></video>
              } @else if (feed.mediaType === 'video' && feed.videoUrl) {
                <video
                  #feedVideo
                  [attr.data-feed-id]="feed.feedID"
                  [src]="mediaUrl(feed.videoUrl)"
                  controls
                  loop
                  muted
                  playsinline
                  preload="none"
                  [poster]="posterUrl(feed.imageUrl || feed.live?.coverImageUrl || feed.listingImageUrl)"
                  (play)="trackView(feed)"
                ></video>
              } @else if (feed.imageUrl || feed.live?.coverImageUrl || feed.listingImageUrl) {
                <app-responsive-image
                  [url]="feed.live?.coverImageUrl || feed.imageUrl || feed.listingImageUrl"
                  [alt]="feed.caption || feed.listingTitle || feed.sellerName"
                  displaySizes="(max-width: 720px) 96vw, 720px"
                />
              } @else {
                <div class="media-placeholder">
                  <span>{{ feed.live?.isLive ? copy().liveNow : copy().feed }}</span>
                </div>
              }
            </div>

            <div class="feed-body">
              @if (feed.caption) {
                <p class="caption">{{ feed.caption }}</p>
              }

              @if (!watchMode && (feed.videoUrl || feed.live?.replayUrl)) {
                <a class="feed-permalink" [routerLink]="feedLink(feed)">{{ copy().watchLive }}</a>
              }

              @if (themeFeedEnabled('show_listing',true) && feed.listingID && feed.listingTitle) {
                <a class="listing-strip" [routerLink]="listingLink(feed)">
                  <span>
                    <small>{{ copy().fortuneProduct }}</small>
                    <strong>{{ feed.listingTitle }}</strong>
                  </span>
                  @if (feed.listingPrice !== null) {
                    <b>{{ feed.listingPrice | number: '1.0-2' }} {{ feed.currencyCode }}</b>
                  }
                </a>
              }

              @if (feed.live?.isLive) {
                <p class="live-inline-note">{{ copy().watchLive }}</p>
              } @else if (feed.live?.replayUrl) {
                <p class="live-inline-note">Canlı yayın sona erdi · Kaydı izle</p>
              }
            </div>

            <footer class="feed-actions">
              @if (themeFeedEnabled('show_engagement',true)) {
              @if (site.enabled('auth') && feed.canLike) {
                <button type="button" [class.active]="feed.isLiked" (click)="toggleLike(feed)">
                  <span>{{ feed.isLiked ? '♥' : '♡' }}</span>
                  {{ feed.likeCount }}
                </button>
              } @else if (feed.likeCountAvailable) {
                <span class="engagement-stat" [attr.aria-label]="likeMetricLabel(feed)" [title]="likeMetricLabel(feed)">
                  <span>♥</span>
                  {{ feed.likeCount }}
                </span>
              }
              <button type="button" (click)="toggleComments(feed)" [attr.aria-expanded]="openedFeedID() === feed.feedID">
                <span>☷</span>
                {{ feed.commentCount }} {{ copy().comment }}
              </button>
              }
              @if (feed.sourcePermalink) {
                <a class="provider-link" [href]="feed.sourcePermalink" target="_blank" rel="noopener noreferrer ugc">
                  {{ isInstagram(feed) ? instagramPostLabel() : copy().sourceLink }}
                </a>
              }
              @if (feed.listingID || (feed.live?.isLive && site.enabled('commerce'))) {
                <a [routerLink]="listingLink(feed)" class="soft-link">
                  {{ copy().goToFortune }}
                </a>
              }
            </footer>

            @if (openedFeedID() === feed.feedID) {
              <section class="comments-panel">
                @if (commentState(feed).loading) {
                  <p class="muted">{{ copy().commentsLoading }}</p>
                }

                @for (comment of commentState(feed).items; track comment.commentID) {
                  <div class="comment">
                    <strong>{{ comment.authorName || copy().guest }}</strong>
                    <p>{{ comment.comment }}</p>
                  </div>
                } @empty {
                  @if (!commentState(feed).loading) {
                    <p class="muted">{{ copy().noComments }}</p>
                  }
                }

                @if (commentState(feed).error) {
                  <p class="error">{{ commentState(feed).error }}</p>
                }

                @if (isInstagram(feed) && feed.sourcePermalink) {
                  <a class="instagram-comment-link" [href]="feed.sourcePermalink" target="_blank" rel="noopener noreferrer ugc">Bu gönderiye Instagram'dan yorum yazabilirsin.</a>
                } @else if (site.enabled('auth')) {
                  <form (submit)="submitComment($event, feed)">
                    <input
                      type="text"
                      [value]="commentState(feed).text"
                      [placeholder]="copy().commentPlaceholder"
                      (input)="setCommentText(feed, $any($event.target).value)"
                    />
                    <button type="submit" [disabled]="commentState(feed).submitting">
                      {{ copy().send }}
                    </button>
                  </form>
                }
              </section>
            }
          </article>
        } @empty {
          <div class="empty-state">
            <h2>{{ copy().emptyTitle }}</h2>
            <p>{{ copy().emptyText }}</p>
          </div>
        }
      </div>

      @if (!watchMode) {
        @if (listMode() === 'infinite') {
          <div #feedSentinel class="feed-sentinel" aria-hidden="true">
            @if (loading()) { <span>{{ copy().loading }}</span> }
            @else if (hasMore()) { <span>{{ copy().autoLoad }}</span> }
            @else if (items().length) { <span>{{ copy().noMore }}</span> }
          </div>
        } @else {
          <nav class="theme-list-pagination" aria-label="Akış sayfaları">
            <button type="button" [disabled]="loading() || page() <= 1" (click)="openPage(page()-1)">← Önceki</button>
            @for (number of paginationPages(); track number) { <button type="button" [class.active]="number===page()" [attr.aria-current]="number===page()?'page':null" [disabled]="loading()" (click)="openPage(number)">{{number}}</button> }
            <button type="button" [disabled]="loading() || !hasMore()" (click)="openPage(page()+1)">Sonraki →</button>
          </nav>
        }
      }
    </section>
  `,
  styleUrl: './feeds-widget.component.scss'
})
export class FeedsWidgetComponent implements AfterViewInit, OnDestroy, OnInit {
  @Input() embedded = false;
  @Input() feedID = 0;
  @Input() watchMode = false;
  private readonly feeds = inject(FeedsService);
  private readonly api = inject(ApiClientService);
  private readonly seo = inject(SeoService);
  private readonly pageData = inject(PageDataService);
  private readonly themeLists = inject(ThemeListExperienceService);
  protected readonly calls = inject(LiveCallService);
  protected readonly i18n = inject(I18nService);
  protected readonly site = inject(SiteConfigService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  @ViewChildren('feedVideo') private readonly videos?: QueryList<ElementRef<HTMLVideoElement>>;
  @ViewChild('feedSentinel') private readonly sentinel?: ElementRef<HTMLElement>;

  protected readonly items = signal<FeedItem[]>([]);
  protected readonly themeFeedSettings = computed<Record<string,unknown>>(() => {
    const presentation=this.pageData.get<{page?:{sections?:Array<{type?:string;settings?:Record<string,unknown>}>}}>('themePresentation');
    return presentation?.page?.sections?.find((section)=>section.type==='feed-stream')?.settings ?? {};
  });
  protected readonly visibleItems = computed(() => this.watchMode ? this.items().slice(0,1) : this.embedded ? this.items().slice(0,Math.max(2,Math.min(24,Number(this.themeFeedSettings()['limit']??12)))) : this.items());
  protected readonly page = signal(1);
  protected readonly limit = signal(12);
  protected readonly hasMore = signal(false);
  protected readonly total = signal(0);
  protected readonly listMode = computed(() => this.themeLists.get('feeds').mode);
  protected readonly paginationPages = computed(() => {const totalPages=Math.max(1,Math.ceil(this.total()/this.limit()));const current=this.page();const start=Math.max(1,Math.min(current-2,totalPages-4));return Array.from({length:Math.min(5,totalPages)},(_,index)=>start+index)});
  protected readonly loading = signal(false);
  protected readonly openedFeedID = signal(0);
  protected readonly commentStates = signal<Record<number, CommentState>>({});
  protected readonly availableCalls = signal<Record<number, any>>({});
  protected readonly watchTitle = computed(() => this.items()[0]?.caption || this.items()[0]?.listingTitle || this.copy().watchLive);
  protected readonly watchDescription = computed(() => this.items()[0]?.caption || this.copy().lead);
  private readonly viewed = new Set<number>();
  private videoObserver?: IntersectionObserver;
  private sentinelObserver?: IntersectionObserver;

  protected readonly copy = computed(() => {
    const brandKey = this.site.config().brandKey;
    const falDeryasi = this.site.experience('fortune');
    if (brandKey === 'umutdemironline' || brandKey === 'fmoshair' || brandKey === 'utkusahincoiffeur') {
      const fmos=brandKey === 'fmoshair';
      const utku=brandKey === 'utkusahincoiffeur';
      return {
        eyebrow: utku?'Dönüşümler':'Stil Günlüğü',
        title: utku?'Kaynak ve renkte imza sonuçlar':fmos?'FMOS’tan son görünümler':'Salondan ilham veren dönüşümler',
        lead: utku?'Mikro ve medikal kaynak, renklendirme ve saç tasarımı çalışmalarımızdan gerçek sonuçları keşfet.':fmos?'Renklendirme, kesim, bakım ve saç tasarımı çalışmalarımızdan güncel paylaşımları keşfet.':'Kesim, renklendirme ve bakım çalışmalarımızdan seçilen güncel paylaşımları keşfet.',
        unknownSeller: utku?'Utku Şahin Coiffeur':fmos?'FMOS Hair':'Umut Demir',
        reader: 'stil ekibi',
        followReader: 'Takip et',
        following: 'Takiptesin',
        liveNow: 'CANLI',
        viewer: 'izleyici',
        feed: 'Stil akışı',
        fortuneProduct: 'Hizmet',
        watchLive: 'Canlı yayını izle',
        comment: 'yorum',
        goToFortune: 'Hizmeti incele',
        sourceLink: 'Instagram gönderisini aç',
        commentsLoading: 'Yorumlar yükleniyor...',
        noComments: 'İlk yorumu sen yaz.',
        commentPlaceholder: 'Yorum yaz...',
        guest: 'Misafir',
        send: 'Gönder',
        emptyTitle: 'Yeni paylaşımlar hazırlanıyor.',
        emptyText: 'Salondan güncel çalışmalar burada yayınlanacak.',
        loading: 'Yükleniyor...',
        loadMore: 'Daha fazla göster',
        autoLoad: 'Yeni paylaşımlar hazırlanıyor...',
        noMore: this.i18n.t('noMoreItems')
      };
    }

    return falDeryasi ? this.falDeryasiCopy() : {
      eyebrow: 'Aymudo Feed',
      title: 'Satici akisindan secilenler',
      lead: 'Magazalardan gelen gorsel, video ve canli yayin paylasimlarini takip et.',
      unknownSeller: 'Satici',
      reader: 'satici',
      followReader: 'Takip et',
      following: 'Takiptesin',
      liveNow: 'LIVE',
      viewer: 'izleyici',
      feed: 'Feed',
      fortuneProduct: 'Urun',
      watchLive: 'Canli yayini izle',
      sourceLink: 'Kaynak gönderiyi aç',
      comment: 'yorum',
      goToFortune: 'Urune git',
      commentsLoading: 'Yorumlar yukleniyor...',
      noComments: 'Ilk yorumu sen yaz.',
      commentPlaceholder: 'Yorum yaz...',
      guest: 'Misafir',
      send: 'Gonder',
      emptyTitle: 'Akis bos.',
      emptyText: 'Paylasimlar geldikce burada gorunecek.',
      loading: 'Yukleniyor...',
      loadMore: 'Daha fazla goster',
      autoLoad: 'Yeni paylasimlar hazirlaniyor...',
      noMore: this.i18n.t('noMoreItems')
    };
  });

  private falDeryasiCopy() {
    const copies = {
      tr: {
        eyebrow: 'Fal Deryası Akışı', title: 'Falcıların anlık paylaşımları',
        lead: 'Canlı yayınları, fal paylaşımlarını ve falcılardan gelen enerjiyi tek akışta takip et.',
        unknownSeller: 'Falcı', reader: 'falcı', followReader: 'Falcıyı takip et', following: 'Takiptesin',
        liveNow: 'CANLI', viewer: 'izleyici', feed: 'Akış', fortuneProduct: 'Fal seçimi',
        watchLive: 'Canlı yayını izle', sourceLink: 'Gönderinin kaynağını aç', comment: 'yorum',
        goToFortune: 'Fala git', commentsLoading: 'Yorumlar yükleniyor...', noComments: 'İlk yorumu sen yaz.',
        commentPlaceholder: 'Falcıya yorum yaz...', guest: 'Misafir', send: 'Gönder',
        emptyTitle: 'Akış henüz sakin.', emptyText: 'Falcılar paylaşım yaptıkça burada görünecek.',
        loading: 'Yükleniyor...', loadMore: 'Daha fazla göster', autoLoad: 'Yeni paylaşımlar hazırlanıyor...'
      },
      en: {
        eyebrow: 'Fal Deryası Feed', title: 'Latest posts from readers',
        lead: 'Follow live sessions, reading posts and fresh energy from readers in one feed.',
        unknownSeller: 'Reader', reader: 'reader', followReader: 'Follow reader', following: 'Following',
        liveNow: 'LIVE', viewer: 'viewer', feed: 'Feed', fortuneProduct: 'Reading',
        watchLive: 'Watch live', sourceLink: 'Open original post', comment: 'comment',
        goToFortune: 'View reading', commentsLoading: 'Loading comments...', noComments: 'Be the first to comment.',
        commentPlaceholder: 'Write a comment...', guest: 'Guest', send: 'Send',
        emptyTitle: 'The feed is quiet for now.', emptyText: 'Reader posts will appear here.',
        loading: 'Loading...', loadMore: 'Show more', autoLoad: 'Preparing more posts...'
      },
      de: {
        eyebrow: 'Fal Deryası Feed', title: 'Aktuelle Beiträge der Berater',
        lead: 'Verfolge Live-Sitzungen, Beiträge und neue Impulse deiner Berater in einem Feed.',
        unknownSeller: 'Berater', reader: 'Berater', followReader: 'Berater folgen', following: 'Gefolgt',
        liveNow: 'LIVE', viewer: 'Zuschauer', feed: 'Feed', fortuneProduct: 'Deutung',
        watchLive: 'Live ansehen', sourceLink: 'Originalbeitrag öffnen', comment: 'Kommentar',
        goToFortune: 'Deutung ansehen', commentsLoading: 'Kommentare werden geladen...', noComments: 'Schreibe den ersten Kommentar.',
        commentPlaceholder: 'Kommentar schreiben...', guest: 'Gast', send: 'Senden',
        emptyTitle: 'Im Feed ist es noch ruhig.', emptyText: 'Neue Beiträge der Berater erscheinen hier.',
        loading: 'Wird geladen...', loadMore: 'Mehr anzeigen', autoLoad: 'Weitere Beiträge werden vorbereitet...'
      },
      es: {
        eyebrow: 'Feed de Fal Deryası', title: 'Últimas publicaciones de videntes',
        lead: 'Sigue directos, publicaciones y nuevas energías de tus videntes en un solo feed.',
        unknownSeller: 'Vidente', reader: 'vidente', followReader: 'Seguir vidente', following: 'Siguiendo',
        liveNow: 'EN DIRECTO', viewer: 'espectador', feed: 'Feed', fortuneProduct: 'Lectura',
        watchLive: 'Ver directo', sourceLink: 'Abrir publicación original', comment: 'comentario',
        goToFortune: 'Ver lectura', commentsLoading: 'Cargando comentarios...', noComments: 'Escribe el primer comentario.',
        commentPlaceholder: 'Escribe un comentario...', guest: 'Invitado', send: 'Enviar',
        emptyTitle: 'El feed está tranquilo.', emptyText: 'Las publicaciones aparecerán aquí.',
        loading: 'Cargando...', loadMore: 'Ver más', autoLoad: 'Preparando más publicaciones...'
      },
      fr: {
        eyebrow: 'Fil Fal Deryası', title: 'Dernières publications des voyants',
        lead: 'Suivez les directs, publications et nouvelles énergies de vos voyants dans un seul fil.',
        unknownSeller: 'Voyant', reader: 'voyant', followReader: 'Suivre le voyant', following: 'Suivi',
        liveNow: 'EN DIRECT', viewer: 'spectateur', feed: 'Fil', fortuneProduct: 'Voyance',
        watchLive: 'Voir le direct', sourceLink: 'Ouvrir la publication originale', comment: 'commentaire',
        goToFortune: 'Voir la voyance', commentsLoading: 'Chargement des commentaires...', noComments: 'Écrivez le premier commentaire.',
        commentPlaceholder: 'Écrire un commentaire...', guest: 'Invité', send: 'Envoyer',
        emptyTitle: 'Le fil est encore calme.', emptyText: 'Les publications des voyants apparaîtront ici.',
        loading: 'Chargement...', loadMore: 'Afficher plus', autoLoad: 'Préparation des publications...'
      }
    };
    return { ...(copies[this.i18n.lang()] ?? copies.en), noMore: this.i18n.t('noMoreItems') };
  }

  ngOnInit(): void {
    const configuredLimit=this.embedded?Number(this.themeFeedSettings()['limit']??0):this.themeLists.get('feeds').pageSize;if(!this.watchMode&&configuredLimit>0)this.limit.set(Math.max(2,Math.min(48,configuredLimit)));
    void this.loadInitial();
    void this.loadCallAvailability();
  }

  protected themeFeedText(key:string,fallback:string):string { const value=this.themeFeedSettings()[key];return typeof value==='string'&&value.trim()?value:fallback; }
  protected themeFeedEnabled(key:string,fallback:boolean):boolean { const value=this.themeFeedSettings()[key];return value===undefined?fallback:value!==false; }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.setupVideoObserver();
    this.setupSentinelObserver();
    this.videos?.changes.subscribe(() => this.setupVideoObserver());
  }

  ngOnDestroy(): void {
    this.videoObserver?.disconnect();
    this.sentinelObserver?.disconnect();
  }

  protected sellerLink(feed: FeedItem): string {
    if (!this.site.enabled('marketplace')) {
      return this.site.config().routes['home'] ?? '/';
    }
    return feed.sellerHandle
      ? this.i18n.routePath('seller', { slug: feed.sellerHandle })
      : this.i18n.routePath('sellers');
  }

  protected listingLink(feed: FeedItem): string {
    return feed.listingSlug
      ? this.i18n.routePath('listing', { slug: feed.listingSlug })
      : this.i18n.routePath('fortune');
  }

  protected feedLink(feed: FeedItem): string {
    const prefix = this.i18n.lang() === 'tr' ? 'akis' : 'feed';
    const localePrefix = this.i18n.locale() === 'tr' ? '' : `/${this.i18n.locale()}`;
    return `${localePrefix}/${prefix}/${feed.feedID}`;
  }

  protected isInstagram(feed: FeedItem): boolean {
    return feed.sourceProvider?.toLowerCase() === 'instagram'
      || /(^|\.)instagram\.com\//i.test(feed.sourcePermalink ?? '');
  }

  protected instagramPostLabel(): string {
    return ({ tr: 'Instagram\'da postu gör', en: 'View post on Instagram', de: 'Post auf Instagram ansehen', es: 'Ver publicación en Instagram', fr: 'Voir le post sur Instagram' } as Record<string, string>)[this.i18n.lang()] ?? 'View post on Instagram';
  }

  protected likeMetricLabel(feed:FeedItem):string {
    const provider=feed.likeCountSource==='instagram'?'Instagram':feed.likeCountSource==='facebook'?'Facebook':'Aymudo';
    const label=({tr:'beğeni',en:'likes',de:'Gefällt mir',es:'Me gusta',fr:'J’aime'} as Record<string,string>)[this.i18n.lang()]??'likes';
    return `${provider}: ${feed.likeCount} ${label}`;
  }

  protected initials(name: string): string {
    return name.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'FD';
  }
  protected feedSellerName(feed:FeedItem):string { return this.site.config().brandKey==='utkusahincoiffeur' ? 'Utku Şahin Coiffeur' : (feed.sellerName || this.copy().unknownSeller); }
  protected callAvailability(sellerID: number): any|null { return this.availableCalls()[sellerID] || null; }

  private async loadCallAvailability(): Promise<void> {
    if (!this.isBrowser) return;
    await this.site.ready();
    if (!this.site.experience('fortune')) return;
    try {
      const response: any = await firstValueFrom(this.api.post('liveCallAvailableSellers', { limit: 30 }));
      const data = response?.data?.data ?? response?.data ?? response ?? {};
      const map: Record<number, any> = {};
      for (const item of Array.isArray(data.items) ? data.items : []) map[Number(item.sellerID)] = item;
      this.availableCalls.set(map);
    } catch { this.availableCalls.set({}); }
  }

  protected mediaUrl(url: string | null | undefined): string {
    if (!url) {
      return '';
    }
    if (/^https?:\/\//.test(url) || url.startsWith('/')) {
      return url;
    }
    return `/${url}`;
  }

  protected posterUrl(url: string | null | undefined): string {
    return this.mediaUrl(this.mediaSizeUrl(url, this.watchMode ? 'b' : 's'));
  }

  private mediaSizeUrl(url: string | null | undefined, size: string): string | null {
    if (!url) {
      return null;
    }
    return url.includes('[size]') ? url.replace('[size]', size) : url;
  }

  protected async loadMore(): Promise<void> {
    if (this.loading() || !this.hasMore()) {
      return;
    }
    this.loading.set(true);
    try {
      const nextPage = this.page() + 1;
      const result = await this.feeds.list({ page: nextPage, limit: this.limit() });
      const existingIDs = new Set(this.items().map((item) => item.feedID));
      const freshItems = result.items.filter((item) => !existingIDs.has(item.feedID));
      this.items.update((items) => [...items, ...freshItems]);
      this.page.set(result.page || nextPage);
      this.hasMore.set(result.hasMore && freshItems.length > 0);
    } finally {
      this.loading.set(false);
    }
  }

  protected async openPage(target:number):Promise<void>{if(this.listMode()!=='pagination'||this.loading()||target<1||target===this.page())return;this.loading.set(true);try{const result=await this.feeds.list({page:target,limit:this.limit()});this.items.set(result.items);this.page.set(result.page||target);this.total.set(result.total);this.hasMore.set(result.hasMore);if(typeof window!=='undefined')window.scrollTo({top:0,behavior:'smooth'})}finally{this.loading.set(false)}}

  protected async toggleLike(feed: FeedItem): Promise<void> {
    if (!feed.canLike) return;
    const like = !feed.isLiked;
    this.patchFeed(feed.feedID, {
      isLiked: like,
      likeCount: Math.max(0, feed.likeCount + (like ? 1 : -1))
    });
    try {
      const result = await this.feeds.like(feed, like);
      this.patchFeed(feed.feedID, result);
    } catch {
      this.patchFeed(feed.feedID, {
        isLiked: feed.isLiked,
        likeCount: feed.likeCount
      });
    }
  }

  protected async toggleFollow(feed: FeedItem): Promise<void> {
    const follow = !feed.isFollowing;
    this.patchSellerFollow(feed.sellerID, follow);
    try {
      const result = await this.feeds.follow(feed, follow);
      this.patchSellerFollow(feed.sellerID, result);
    } catch {
      this.patchSellerFollow(feed.sellerID, feed.isFollowing);
    }
  }

  protected async toggleComments(feed: FeedItem): Promise<void> {
    if (this.openedFeedID() === feed.feedID) {
      this.openedFeedID.set(0);
      return;
    }
    this.openedFeedID.set(feed.feedID);
    const state = this.commentState(feed);
    if (state.items.length || state.loading) {
      return;
    }
    this.setCommentState(feed.feedID, { ...state, loading: true, error: '' });
    try {
      const result = await this.feeds.comments(feed.feedID);
      this.patchFeed(feed.feedID, { commentCount: result.total });
      this.setCommentState(feed.feedID, {
        ...this.commentState(feed),
        loading: false,
        items: result.comments,
        hasMore: result.hasMore
      });
    } catch {
      this.setCommentState(feed.feedID, {
        ...this.commentState(feed),
        loading: false,
        error: 'Yorumlar alinamadi.'
      });
    }
  }

  protected setCommentText(feed: FeedItem, text: string): void {
    this.setCommentState(feed.feedID, {
      ...this.commentState(feed),
      text
    });
  }

  protected async submitComment(event: Event, feed: FeedItem): Promise<void> {
    event.preventDefault();
    const state = this.commentState(feed);
    const text = state.text.trim();
    if (!text || state.submitting) {
      return;
    }
    this.setCommentState(feed.feedID, { ...state, submitting: true, error: '' });
    try {
      const comment = await this.feeds.comment(feed.feedID, text);
      if (comment) {
        this.setCommentState(feed.feedID, {
          ...this.commentState(feed),
          submitting: false,
          text: '',
          items: [comment, ...this.commentState(feed).items]
        });
        this.patchFeed(feed.feedID, { commentCount: feed.commentCount + 1 });
      }
    } catch {
      this.setCommentState(feed.feedID, {
        ...this.commentState(feed),
        submitting: false,
        error: 'Yorum gonderilemedi. Giris yapman gerekebilir.'
      });
    }
  }

  protected trackView(feed: FeedItem): void {
    if (this.viewed.has(feed.feedID)) {
      return;
    }
    this.viewed.add(feed.feedID);
    void this.feeds.view(feed.feedID);
  }

  protected commentState(feed: FeedItem): CommentState {
    return this.commentStates()[feed.feedID] ?? this.emptyCommentState();
  }

  private async loadInitial(): Promise<void> {
    this.loading.set(true);
    try {
      const result = this.feedID > 0
        ? await this.feeds.list({ page: 1, limit: 1, feedID: this.feedID })
        : await this.feeds.initial(this.limit());
      this.items.set(result.items);
      this.page.set(result.page);
      this.limit.set(result.limit);
      this.hasMore.set(result.hasMore);
      this.total.set(result.total);
      if (this.watchMode && result.items[0]) {
        this.setVideoSeo(result.items[0]);
      }
    } finally {
      this.loading.set(false);
    }
  }

  private setVideoSeo(feed: FeedItem): void {
    const videoUrl = feed.live?.replayUrl || feed.videoUrl;
    const thumbnail = this.posterUrl(feed.imageUrl || feed.live?.coverImageUrl || feed.listingImageUrl);
    if (!videoUrl || !thumbnail) return;
    const duration = Math.max(0, Number(feed.live?.recordingDurationSeconds || feed.durationSeconds || 0));
    const title = feed.caption || feed.listingTitle || `${feed.sellerName} video paylaşımı`;
    const path = this.feedLink(feed).replace(/^\/(?:en-us|en-gb|de-de|es-es|fr-fr)/, '');
    this.seo.setPage({
      title,
      description: feed.caption || `${feed.sellerName} tarafından paylaşılan videoyu izle.`,
      image: thumbnail,
      path,
      schema: {
        '@type': 'VideoObject',
        name: title,
        description: feed.caption || title,
        thumbnailUrl: [this.absoluteMedia(thumbnail)],
        uploadDate: feed.publishedAt || feed.live?.startedAt || new Date().toISOString(),
        contentUrl: this.absoluteMedia(videoUrl),
        embedUrl: `${this.site.config().apiBaseUrl}${this.feedLink(feed)}`,
        ...(duration > 0 ? { duration: `PT${duration}S` } : {})
      }
    });
  }

  private absoluteMedia(url: string): string {
    if (/^https?:\/\//i.test(url)) return url;
    return `${this.site.config().apiBaseUrl}${url.startsWith('/') ? url : `/${url}`}`;
  }

  private patchFeed(feedID: number, patch: Partial<FeedItem>): void {
    this.items.update((items) => items.map((item) => item.feedID === feedID ? { ...item, ...patch } : item));
  }

  private patchSellerFollow(sellerID: number, isFollowing: boolean): void {
    this.items.update((items) => items.map((item) => item.sellerID === sellerID ? { ...item, isFollowing } : item));
  }

  private setupVideoObserver(): void {
    this.videoObserver?.disconnect();
    if (!this.isBrowser) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      for (const video of this.videos?.toArray() ?? []) {
        if (typeof video.nativeElement.play === 'function') {
          void video.nativeElement.play().catch(() => undefined);
        }
      }
      return;
    }

    this.videoObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const video = entry.target as HTMLVideoElement;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.58) {
          this.pauseOtherVideos(video);
          video.muted = true;
          video.playsInline = true;
          void video.play().catch(() => undefined);
          this.trackVisibleVideo(video);
        } else {
          video.pause();
        }
      }
    }, {
      threshold: [0, .35, .58, .82],
      rootMargin: '-12% 0px -18% 0px'
    });

    for (const video of this.videos?.toArray() ?? []) {
      this.videoObserver.observe(video.nativeElement);
    }
  }

  private setupSentinelObserver(): void {
    if (!this.sentinel?.nativeElement || typeof IntersectionObserver === 'undefined') {
      return;
    }
    this.sentinelObserver?.disconnect();
    this.sentinelObserver = new IntersectionObserver((entries) => {
      if (this.listMode()==='infinite' && entries.some((entry) => entry.isIntersecting)) {
        void this.loadMore();
      }
    }, {
      rootMargin: '900px 0px'
    });
    this.sentinelObserver.observe(this.sentinel.nativeElement);
  }

  private pauseOtherVideos(activeVideo: HTMLVideoElement): void {
    for (const video of this.videos?.toArray() ?? []) {
      if (video.nativeElement !== activeVideo) {
        video.nativeElement.pause();
      }
    }
  }

  private trackVisibleVideo(video: HTMLVideoElement): void {
    const feedID = Number(video.dataset['feedId'] ?? 0);
    if (feedID <= 0 || this.viewed.has(feedID)) {
      return;
    }
    this.viewed.add(feedID);
    void this.feeds.view(feedID);
  }

  private setCommentState(feedID: number, state: CommentState): void {
    this.commentStates.update((states) => ({
      ...states,
      [feedID]: state
    }));
  }

  private emptyCommentState(): CommentState {
    return {
      loading: false,
      submitting: false,
      text: '',
      items: [],
      hasMore: false,
      error: ''
    };
  }
}
